import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { buildDefaultModifierSelectionMap, buildSelectedModifierPayload, normalizeModifierSummary } from '../../utils/itemUtils';

const toCurrency = (value) => `${Number(value || 0).toFixed(2)} TRY`;

const ModifierPickerModal = ({ show, item, onClose, onConfirm, title = 'Ürünü Yapılandır' }) => {
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState({});

  const modifierGroups = useMemo(() => item?.modifierGroups || [], [item]);

  useEffect(() => {
    if (!show) {
      return;
    }
    const initialState = buildDefaultModifierSelectionMap(modifierGroups);
    setSelectedOptionsByGroup(initialState);
  }, [modifierGroups, show]);

  const selectedModifierOptions = useMemo(
    () =>
      buildSelectedModifierPayload(
        Object.entries(selectedOptionsByGroup).flatMap(([groupId, optionIds]) =>
          (optionIds || []).map((optionId) => ({
            groupId: Number(groupId),
            optionId: Number(optionId),
          }))
        )
      ),
    [selectedOptionsByGroup]
  );

  const selectedModifiers = useMemo(() => {
    const selectedKeySet = new Set(selectedModifierOptions.map((entry) => `${entry.group_id}:${entry.option_id}`));
    return normalizeModifierSummary(
      modifierGroups.flatMap((group) =>
        (group.options || [])
          .filter((option) => selectedKeySet.has(`${group.id}:${option.id}`))
          .map((option) => ({
            groupName: group.name,
            optionName: option.name,
            priceDelta: Number(option.priceDelta || 0),
          }))
      )
    );
  }, [modifierGroups, selectedModifierOptions]);

  const modifierTotal = useMemo(
    () => selectedModifiers.reduce((total, modifier) => total + Number(modifier.priceDelta || 0), 0),
    [selectedModifiers]
  );

  const validationError = useMemo(() => {
    for (const group of modifierGroups) {
      const count = (selectedOptionsByGroup[group.id] || []).length;
      const min = Number(group.minSelections || 0);
      const max = Number(group.maxSelections || 1);
      if (group.required && count < Math.max(1, min)) {
        return `${group.name} seçimi gerekli.`;
      }
      if (count < min) {
        return `${group.name} için en az ${min} seçim yapın.`;
      }
      if (count > max) {
        return `${group.name} için en fazla ${max} seçim yapabilirsiniz.`;
      }
    }
    return '';
  }, [modifierGroups, selectedOptionsByGroup]);

  const toggleOption = (group, optionId) => {
    setSelectedOptionsByGroup((current) => {
      const existing = current[group.id] || [];
      const maxSelections = Number(group.maxSelections || 1);
      if (maxSelections === 1) {
        return {
          ...current,
          [group.id]: existing.includes(optionId) ? [] : [optionId],
        };
      }
      if (existing.includes(optionId)) {
        return {
          ...current,
          [group.id]: existing.filter((entry) => entry !== optionId),
        };
      }
      if (existing.length >= maxSelections) {
        return current;
      }
      return {
        ...current,
        [group.id]: [...existing, optionId],
      };
    });
  };

  const handleConfirm = () => {
    onConfirm({
      ...item,
      modifiers: selectedModifiers,
      selectedModifierOptions,
      price: Number(item?.price || 0) + modifierTotal,
      basePrice: Number(item?.price || 0),
      modifierTotal,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {item ? (
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="fw-semibold">{item.name}</div>
              <div className="text-muted small">{toCurrency(Number(item.price || 0) + modifierTotal)}</div>
            </div>

            {modifierGroups.map((group) => (
              <div key={group.id} className="border rounded p-3">
                <div className="fw-semibold mb-1">{group.name}</div>
                <div className="small text-muted mb-2">
                  {group.required ? 'Zorunlu' : 'Opsiyonel'} • {group.minSelections}-{group.maxSelections} seçim
                </div>
                <div className="d-flex flex-column gap-2">
                  {(group.options || []).map((option) => {
                    const isSelected = (selectedOptionsByGroup[group.id] || []).includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`btn ${isSelected ? 'btn-success' : 'btn-outline-secondary'} text-start`}
                        onClick={() => toggleOption(group, option.id)}
                      >
                        <span>{option.name}</span>
                        <span className="ms-2">{toCurrency(option.priceDelta || 0)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {validationError ? <div className="text-danger small">{validationError}</div> : null}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Vazgeç
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={Boolean(validationError)}>
          Ürünü Ekle
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModifierPickerModal;
